import * as path from 'node:path';
import type { NameStatusEntry } from '../../changes/parse/types.js';
import { forEachWithGitYield } from '../../git-runtime.js';
import { readGitBlobAsUtf8, readWorkspaceFileAsUtf8 } from './files.js';
import { safeRelPath } from './paths.js';

export type UntrackedMove = {
  oldRel: string;
  oldText: string;
  newText: string;
};

export type UntrackedMovePairResult = {
  untrackedTextByRel: Map<string, string>;
  untrackedMoveByRel: Map<string, UntrackedMove>;
  movedDeletedRels: Set<string>;
};

export async function readUntrackedTexts(repoPath: string, untracked: readonly string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  await forEachWithGitYield(untracked, async (rel) => {
    if (!safeRelPath(rel)) return;
    const newText = await readWorkspaceFileAsUtf8(path.join(repoPath, rel));
    if (newText !== undefined) out.set(rel, newText);
  });
  return out;
}

export async function pairDeletedFilesWithUntrackedMoves(options: {
  repoPath: string;
  baselineRev: string;
  entries: readonly NameStatusEntry[];
  untrackedTextByRel: ReadonlyMap<string, string>;
}): Promise<UntrackedMovePairResult> {
  const { repoPath, baselineRev, entries, untrackedTextByRel } = options;
  const untrackedMoveByRel = new Map<string, UntrackedMove>();
  const movedDeletedRels = new Set<string>();
  const usedMovedUntrackedRels = new Set<string>();

  await forEachWithGitYield(
    entries.filter((entry) => entry.change === 'removed'),
    async (entry) => {
      const oldText = await readGitBlobAsUtf8(repoPath, baselineRev, entry.pathInRepo);
      if (oldText === undefined) return;
      for (const [newRel, newText] of untrackedTextByRel) {
        if (usedMovedUntrackedRels.has(newRel) || newText !== oldText) continue;
        untrackedMoveByRel.set(newRel, { oldRel: entry.pathInRepo, oldText, newText });
        movedDeletedRels.add(entry.pathInRepo);
        usedMovedUntrackedRels.add(newRel);
        break;
      }
    }
  );

  return {
    untrackedTextByRel: new Map(untrackedTextByRel),
    untrackedMoveByRel,
    movedDeletedRels,
  };
}
